var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "nativewind/jsx-runtime";
import Clipboard from '@react-native-clipboard/clipboard';
import { useNavigation } from '@react-navigation/native';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { ScrollView } from 'react-native';
import styled from 'styled-components/native';
import BackIcon from '@/assets/icon/ic_v2_arrow_back.svg';
import BookmarkIcon from '@/assets/icon/ic_bookmark.svg';
import CopyIcon from '@/assets/icon/ic_copy.svg';
import RouteFillIcon from '@/assets/icon/ic_route_fill.svg';
import ShareIcon from '@/assets/icon/ic_share.svg';
import GenderBothIcon from '@/assets/icon/ic_toilet_both.svg';
import DoorAutoIcon from '@/assets/icon/ic_toilet_door_auto.svg';
import DoorFoldIcon from '@/assets/icon/ic_toilet_door_fold.svg';
import DoorSlideIcon from '@/assets/icon/ic_toilet_door_slide.svg';
import DoorSwingIcon from '@/assets/icon/ic_toilet_door_swing.svg';
import DoorUnknownIcon from '@/assets/icon/ic_toilet_door_unknown.svg';
import EntranceSlopeIcon from '@/assets/icon/ic_toilet_entrance_slope.svg';
import EntranceStepIcon from '@/assets/icon/ic_toilet_entrance_step.svg';
import GenderFemaleIcon from '@/assets/icon/ic_toilet_female.svg';
import GenderMaleIcon from '@/assets/icon/ic_toilet_male.svg';
import { LoadingView } from '@/components/LoadingView';
import RoadView from '@/components/maps/RoadView';
import { SccPressable } from '@/components/SccPressable';
import { ScreenLayout } from '@/components/ScreenLayout';
import { SccTouchableOpacity } from '@/components/SccTouchableOpacity';
import { color } from '@/constant/color';
import { font } from '@/constant/font';
import { AccessibilitySourceDto, } from '@/generated-sources/openapi';
import NavigationAppsBottomSheet from '@/screens/PlaceDetailScreen/modals/NavigationAppsBottomSheet';
import useAppComponents from '@/hooks/useAppComponents';
import AvailableLabel from '@/screens/ToiletDetailScreen/AvailableLabel';
import ToiletImageCarousel from '@/screens/ToiletDetailScreen/ToiletImageCarousel';
import { accessibilitySourceLabel, mapToiletDetailsToToiletDetails, } from '@/components/toilet/data';
import ToastUtils from '@/utils/ToastUtils.ts';
const ToiletDetailScreen = ({ route }) => {
    const { toiletId } = route.params;
    const { toiletApi } = useAppComponents();
    const { data, isLoading, isError, error } = useQuery({
        enabled: toiletId != null,
        queryKey: ['toiletDetail', toiletId],
        queryFn: () => __awaiter(void 0, void 0, void 0, function* () {
            return yield toiletApi.getToilet({ toiletId });
        }),
    });
    const detail = data === null || data === void 0 ? void 0 : data.data;
    if (isLoading) {
        return (_jsxs(ScreenLayout, { isHeaderVisible: false, safeAreaEdges: ['top', 'bottom'], children: [_jsx(AppBar, {}), _jsx(LoadingView, {})] }));
    }
    if (isError || detail === undefined) {
        if (error != null) {
            ToastUtils.showOnApiError(error);
        }
        return (_jsxs(ScreenLayout, { isHeaderVisible: false, safeAreaEdges: ['top', 'bottom'], children: [_jsx(AppBar, {}), _jsx(ErrorContainer, { children: _jsx(ErrorText, { children: "\uD654\uC7A5\uC2E4 \uC815\uBCF4\uB97C \uBD88\uB7EC\uC624\uC9C0 \uBABB\uD588\uC2B5\uB2C8\uB2E4." }) })] }));
    }
    return _jsx(ToiletDetail, { detail: detail });
};
const ToiletDetail = ({ detail }) => {
    // 여러 소스(유저 리뷰 N개 + 공공데이터 N개)가 accessibilities 배열로 내려온다.
    // 소스마다 섹션을 반복 렌더하면 같은 섹션 타입이 중복되므로,
    // sourceType 분기 없이 "섹션 타입별 1회"로 집계(aggregate)하여 단일 뷰로 렌더한다.
    var _a, _b, _c, _d, _e;
    // 이미지: 모든 accessibility의 images를 하나로 합쳐 1개 캐러셀로 보여준다.
    const allImages = detail.accessibilities.flatMap(accessibility => accessibility.images);
    // 유저 리뷰 섹션: locationComment / comment를 가진 첫 번째 대표 소스를 사용한다.
    // (등록자/등록시점을 함께 표시하기 위해 값이 아니라 소스 accessibility를 찾는다.)
    const locationCommentSource = detail.accessibilities.find(accessibility => accessibility.locationComment != null);
    const commentSource = detail.accessibilities.find(accessibility => accessibility.comment != null);
    const locationComment = locationCommentSource === null || locationCommentSource === void 0 ? void 0 : locationCommentSource.locationComment;
    const comment = commentSource === null || commentSource === void 0 ? void 0 : commentSource.comment;
    // 등록자/등록시점은 유저 리뷰 소스 기준으로 타이틀 영역에 한 번만 표시한다.
    const reviewSource = locationCommentSource !== null && locationCommentSource !== void 0 ? locationCommentSource : commentSource;
    // 공공데이터 상세(toiletDetails): toiletDetails를 가진 첫 번째 대표 소스만 사용한다.
    // 공공데이터 상세(toiletDetails): toiletDetails를 가진 첫 번째 대표 소스만 사용한다.
    const publicDataSource = detail.accessibilities.find(accessibility => accessibility.toiletDetails != null);
    const representativeToiletDetails = publicDataSource === null || publicDataSource === void 0 ? void 0 : publicDataSource.toiletDetails;
    const publicToiletDetails = representativeToiletDetails != null
        ? mapToiletDetailsToToiletDetails(detail.id, detail.name, (_a = detail.address) !== null && _a !== void 0 ? _a : undefined, detail.location, representativeToiletDetails)
        : undefined;
    // 타이틀 영역의 사용가능 라벨은 공공데이터 상세에서 가져온다.
    const availableForLabel = publicToiletDetails === null || publicToiletDetails === void 0 ? void 0 : publicToiletDetails.available;
    // 로드뷰 노출 조건: PA급 상세 필드 중 하나라도 없으면(hasRichToiletDetail=false) 로드뷰 노출.
    // 공공데이터 전용 필드(openingHours/phoneNumber 등)가 있어도 PA급이 없으면 로드뷰를 띄운다.
    const hasRichToiletDetail = [
        (_b = publicToiletDetails === null || publicToiletDetails === void 0 ? void 0 : publicToiletDetails.stall) === null || _b === void 0 ? void 0 : _b.width,
        (_c = publicToiletDetails === null || publicToiletDetails === void 0 ? void 0 : publicToiletDetails.stall) === null || _c === void 0 ? void 0 : _c.depth,
        (_d = publicToiletDetails === null || publicToiletDetails === void 0 ? void 0 : publicToiletDetails.door) === null || _d === void 0 ? void 0 : _d.desc,
        (_e = publicToiletDetails === null || publicToiletDetails === void 0 ? void 0 : publicToiletDetails.entrance) === null || _e === void 0 ? void 0 : _e.desc,
        publicToiletDetails === null || publicToiletDetails === void 0 ? void 0 : publicToiletDetails.accessDesc,
        publicToiletDetails === null || publicToiletDetails === void 0 ? void 0 : publicToiletDetails.doorSideRoom,
        publicToiletDetails === null || publicToiletDetails === void 0 ? void 0 : publicToiletDetails.washStandBelowRoom,
        publicToiletDetails === null || publicToiletDetails === void 0 ? void 0 : publicToiletDetails.washStandHandle,
    ].some(v => !!v);
    const onCopy = () => {
        if (detail.address) {
            Clipboard.setString(detail.address);
            ToastUtils.show('주소가 복사되었습니다.');
        }
    };
    const onShare = () => {
        ToastUtils.show('준비 중입니다.');
    };
    const onBookmark = () => {
        ToastUtils.show('준비 중입니다.');
    };
    const [showNavigation, setShowNavigation] = useState(false);
    return (_jsxs(ScreenLayout, { isHeaderVisible: false, safeAreaEdges: ['top', 'bottom'], children: [_jsx(AppBar, { name: detail.name }), _jsxs(ScrollView, { children: [allImages.length > 0 && _jsx(ToiletImageCarousel, { images: allImages }), _jsxs(Container, { children: [_jsx(Section, { children: _jsxs(TitleArea, { children: [availableForLabel && (_jsx(AvailableLabel, { availableState: availableForLabel.state, text: availableForLabel.desc })), detail.address != null && (_jsxs(AddressRow, { children: [_jsx(AddressText, { children: detail.address }), _jsxs(CopyButton, { elementName: "toilet_detail_copy_button", onPress: onCopy, children: [_jsx(CopyIcon, {}), _jsx(CopyText, { children: "\uBCF5\uC0AC" })] })] })), detail.location != null && (_jsxs(DirectionsRow, { children: [_jsx(RouteFillIcon, { width: 20, height: 20, color: color.gray30 }), _jsx(SccTouchableOpacity, { elementName: "toilet_detail_directions_button", onPress: () => setShowNavigation(true), children: _jsx(DirectionsText, { children: "\uAE38\uCC3E\uAE30" }) })] })), _jsx(MetaRow, { reviewSource: reviewSource !== null && reviewSource !== void 0 ? reviewSource : null, publicDataSource: publicDataSource !== null && publicDataSource !== void 0 ? publicDataSource : null }), _jsx(SectionDivider, {}), _jsxs(TextButtonContainer, { children: [_jsxs(TextButton, { elementName: "toilet_detail_share_button", onPress: onShare, children: [_jsx(ShareIcon, {}), _jsx(TextButtonText, { children: "\uACF5\uC720" })] }), _jsx(VerticalDivider, {}), _jsxs(TextButton, { elementName: "toilet_detail_bookmark_button", onPress: onBookmark, children: [_jsx(BookmarkIcon, { color: color.gray80 }), _jsx(TextButtonText, { children: "\uC800\uC7A5" })] })] })] }) }), locationComment != null && (_jsxs(Section, { children: [_jsx(SectionTitleText, { children: "\uD654\uC7A5\uC2E4 \uC704\uCE58" }), _jsx(SectionDivider, {}), _jsx(SubSectionDescription, { children: locationComment })] })), comment != null && (_jsxs(Section, { children: [_jsx(SectionTitleText, { children: "\uAE30\uD0C0 \uCC38\uACE0\uC0AC\uD56D" }), _jsx(SectionDivider, {}), _jsx(SubSectionDescription, { children: comment })] })), detail.location != null && !hasRichToiletDetail && (_jsx(RoadView, { position: { lat: detail.location.lat, lng: detail.location.lng }, name: detail.name })), publicToiletDetails != null && (_jsx(ToiletPublicDetailSections, { toiletDetails: publicToiletDetails, showSeoulAttribution: detail.accessibilities.some(a => a.source === AccessibilitySourceDto.SmartSeoulMap) }))] })] }), detail.location != null && (_jsx(NavigationAppsBottomSheet, { isVisible: showNavigation, latitude: detail.location.lat, longitude: detail.location.lng, placeName: detail.name, onClose: () => setShowNavigation(false) }))] }));
};
const ToiletPublicDetailSections = ({ toiletDetails, showSeoulAttribution, }) => {
    var _a, _b, _c, _d;
    const hasUsageInfo = toiletDetails.gender != null ||
        toiletDetails.available != null ||
        toiletDetails.openingHours != null ||
        toiletDetails.phoneNumber != null;
    const hasAccessInfo = ((_a = toiletDetails.entrance) === null || _a === void 0 ? void 0 : _a.state) != null ||
        ((_b = toiletDetails.door) === null || _b === void 0 ? void 0 : _b.state) != null ||
        toiletDetails.accessDesc != null;
    const hasInteriorInfo = toiletDetails.stall != null ||
        toiletDetails.doorSideRoom != null ||
        toiletDetails.washStandBelowRoom != null ||
        toiletDetails.washStandHandle != null;
    return (_jsxs(_Fragment, { children: [hasUsageInfo && (_jsxs(Section, { children: [_jsx(SectionTitleRow, { children: _jsx(SectionTitleText, { children: "\uD654\uC7A5\uC2E4 \uC0AC\uC6A9 \uC815\uBCF4" }) }), _jsx(SectionDivider, {}), toiletDetails.gender && (_jsxs(IconedSection, { children: [_jsxs(SubSection, { children: [_jsx(SubSectionLabel, { children: "\uC774\uC6A9\uC131\uBCC4" }), _jsx(SubSectionTitle, { children: toiletDetails.gender.desc })] }), _jsxs(IconArea, { children: [toiletDetails.gender.state === 'MALE' && _jsx(GenderMaleIcon, {}), toiletDetails.gender.state === 'FEMALE' && (_jsx(GenderFemaleIcon, {})), toiletDetails.gender.state === 'BOTH' && _jsx(GenderBothIcon, {})] })] })), toiletDetails.available && (_jsxs(SubSection, { children: [_jsx(SubSectionLabel, { children: "\uC0AC\uC6A9\uAC00\uB2A5\uC5EC\uBD80" }), _jsx(SubSectionTitle, { children: {
                                    AVAILABLE: '사용가능',
                                    UNAVAILABLE: '사용불가',
                                    UNKNOWN: '알 수 없음',
                                }[toiletDetails.available.state] }), _jsx(SubSectionDescription, { children: toiletDetails.available.desc })] })), toiletDetails.openingHours != null && (_jsxs(SubSection, { children: [_jsx(SubSectionLabel, { children: "\uAC1C\uBC29\uC2DC\uAC04" }), _jsx(SubSectionTitle, { children: toiletDetails.openingHours })] })), toiletDetails.phoneNumber != null && (_jsxs(SubSection, { children: [_jsx(SubSectionLabel, { children: "\uC804\uD654\uBC88\uD638" }), _jsxs(PhoneRow, { children: [_jsx(PhoneNumberText, { children: toiletDetails.phoneNumber }), _jsxs(CopyButton, { elementName: "toilet_detail_phone_copy", onPress: () => {
                                            Clipboard.setString(toiletDetails.phoneNumber);
                                            ToastUtils.show('전화번호가 복사되었습니다.');
                                        }, children: [_jsx(CopyIcon, {}), _jsx(CopyText, { children: "\uBCF5\uC0AC" })] })] })] }))] })), hasAccessInfo && (_jsxs(Section, { children: [_jsx(SectionTitleText, { children: "\uD654\uC7A5\uC2E4 \uC811\uADFC \uC815\uBCF4" }), _jsx(SectionDivider, {}), ((_c = toiletDetails.entrance) === null || _c === void 0 ? void 0 : _c.state) && (_jsxs(IconedSection, { children: [_jsxs(SubSection, { children: [_jsx(SubSectionLabel, { children: "\uD654\uC7A5\uC2E4 \uC785\uAD6C" }), _jsx(SubSectionTitle, { children: toiletDetails.entrance.desc })] }), _jsxs(IconArea, { children: [toiletDetails.entrance.state === 'STEP' && (_jsx(EntranceStepIcon, {})), toiletDetails.entrance.state === 'SLOPE' && (_jsx(EntranceSlopeIcon, {}))] })] })), ((_d = toiletDetails.door) === null || _d === void 0 ? void 0 : _d.state) && (_jsxs(IconedSection, { children: [_jsxs(SubSection, { children: [_jsx(SubSectionLabel, { children: "\uD654\uC7A5\uC2E4 \uCD9C\uC785\uBB38" }), _jsx(SubSectionTitle, { children: toiletDetails.door.desc })] }), _jsxs(IconArea, { children: [toiletDetails.door.state === 'AUTO' && _jsx(DoorAutoIcon, {}), toiletDetails.door.state === 'SLIDE' && _jsx(DoorSlideIcon, {}), toiletDetails.door.state === 'SWING' && _jsx(DoorSwingIcon, {}), toiletDetails.door.state === 'FOLD' && _jsx(DoorFoldIcon, {}), toiletDetails.door.state === 'UNKNOWN' && _jsx(DoorUnknownIcon, {})] })] })), toiletDetails.accessDesc && (_jsxs(RecommendedAccessRoute, { children: [_jsx(SubSectionTitle, { style: { marginTop: 0 }, children: "\uCD94\uCC9C \uC811\uADFC \uACBD\uB85C\uD83D\uDCA1" }), _jsx(SubSectionDescription, { children: toiletDetails.accessDesc })] }))] })), hasInteriorInfo && (_jsxs(Section, { children: [_jsx(SectionTitleText, { children: "\uD654\uC7A5\uC2E4 \uB0B4\uBD80 \uC815\uBCF4" }), _jsx(SectionDivider, {}), toiletDetails.stall && (_jsxs(SubSection, { children: [_jsx(SubSectionLabel, { children: "\uB0B4\uBD80\uACF5\uAC04 \uC0AC\uC774\uC988" }), _jsxs(SubSectionTitle, { children: ["\uAC00\uB85C ", toiletDetails.stall.width] }), _jsxs(SubSectionTitle, { children: ["\uC138\uB85C ", toiletDetails.stall.depth] })] })), toiletDetails.doorSideRoom && (_jsxs(SubSection, { children: [_jsx(SubSectionLabel, { children: "\uB300\uBCC0\uAE30 \uC606 \uACF5\uAC04 \uC5EC\uBD80" }), _jsx(SubSectionTitle, { children: toiletDetails.doorSideRoom })] })), toiletDetails.washStandBelowRoom && (_jsxs(SubSection, { children: [_jsx(SubSectionLabel, { children: "\uC138\uBA74\uB300 \uC544\uB798 \uACF5\uAC04" }), _jsx(SubSectionTitle, { children: toiletDetails.washStandBelowRoom })] })), toiletDetails.washStandHandle && (_jsxs(SubSection, { children: [_jsx(SubSectionLabel, { children: "\uC138\uBA74\uB300 \uC190\uC7A1\uC774" }), _jsx(SubSectionTitle, { children: toiletDetails.washStandHandle })] }))] })), toiletDetails.extra && (_jsxs(Section, { children: [_jsx(SectionTitleText, { children: "\uAE30\uD0C0 \uCC38\uACE0\uC0AC\uD56D" }), _jsx(ExtraInfos, { children: _jsx(SubSectionDescription, { children: toiletDetails.extra }) })] })), showSeoulAttribution && (_jsx(Footer, { children: _jsx(FooterText, { children: "\uBCF8 \uC800\uC791\uBB3C\uC740 \u2018\uC2A4\uB9C8\uD2B8 \uC11C\uC6B8\uB9F5 - (\uB3D9\uD589)\uD720\uCCB4\uC5B4\uB3C4 \uAC00\uB294 \uD654\uC7A5\uC2E4 \uC9C0\uB3C4'\uB97C \uC774\uC6A9\uD558\uC600\uC2B5\uB2C8\uB2E4." }) }))] }));
};
/**
 * 타이틀 영역 메타 행: 리뷰 소스가 있으면 등록자+등록일, 없고 공공데이터 소스만 있으면 출처+확인일.
 * 어떤 경우든 meta 행은 1개만 렌더한다.
 */
function MetaRow({ reviewSource, publicDataSource, }) {
    var _a;
    if (reviewSource != null) {
        const userName = (_a = reviewSource.registeredUserName) !== null && _a !== void 0 ? _a : '익명';
        const dateStr = reviewSource.createdAt
            ? dayjs(reviewSource.createdAt.value).format('YYYY.MM.DD')
            : null;
        return (_jsxs(RegistrantRow, { children: [_jsx(RegistrantName, { children: userName }), dateStr != null && _jsx(RegistrantDate, { children: dateStr })] }));
    }
    if ((publicDataSource === null || publicDataSource === void 0 ? void 0 : publicDataSource.source) != null) {
        const sourceName = accessibilitySourceLabel(publicDataSource.source);
        const dateStr = publicDataSource.lastVerifiedAt
            ? dayjs(publicDataSource.lastVerifiedAt.value).format('YYYY.MM.DD')
            : null;
        return (_jsxs(RegistrantRow, { children: [_jsx(RegistrantName, { children: sourceName }), dateStr != null && _jsxs(RegistrantDate, { children: [dateStr, " \uD655\uC778"] })] }));
    }
    return null;
}
function AppBar({ name }) {
    const navigation = useNavigation();
    return (_jsxs(AppBarContainer, { children: [_jsx(SccPressable, { elementName: "toilet_detail_back_button", hitSlop: 10, onPress: () => navigation.goBack(), children: _jsx(BackIcon, { width: 24, height: 24, color: color.black }) }), _jsx(AppBarTitleContainer, { children: name ? (_jsx(AppBarTitleText, { numberOfLines: 1, children: name })) : null })] }));
}
const AppBarContainer = styled.View `
  flex-direction: row;
  align-items: center;
  height: 50px;
  padding: 0 20px;
  background-color: ${color.white};
`;
const AppBarTitleContainer = styled.View `
  flex: 1;
  justify-content: center;
  padding: 0 8px;
`;
const AppBarTitleText = styled.Text `
  font-size: 18px;
  line-height: 26px;
  font-family: ${() => font.pretendardMedium};
  color: ${color.black};
`;
const ErrorContainer = styled.View `
  flex: 1;
  align-items: center;
  justify-content: center;
  padding: 30px;
`;
const ErrorText = styled.Text `
  font-size: 16px;
  font-family: ${() => font.pretendardRegular};
  color: ${color.gray80};
`;
const Container = styled.View `
  flex: 1;
  gap: 13px;
  background-color: ${color.gray10};
`;
const SectionTitleText = styled.Text `
  font-size: 18px;
  font-family: ${() => font.pretendardBold};
  color: ${color.black};
`;
const SectionTitleRow = styled.View `
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;
const SectionDivider = styled.View `
  height: 1px;
  background-color: ${color.gray20};
  width: 100%;
`;
const Section = styled.View `
  background-color: white;
  padding: 30px 30px 20px;
  flex-direction: column;
  gap: 20px;
  align-items: flex-start;
`;
const IconedSection = styled.View `
  width: 100%;
  flex-direction: row;
  align-items: center;
  gap: 10px;
`;
const IconArea = styled.View `
  width: 58px;
  height: 58px;
  background-color: ${color.gray10};
  border-radius: 14px;
  align-items: center;
  justify-content: center;
`;
const SubSection = styled.View `
  flex-grow: 1;
  flex-direction: column;
  align-items: flex-start;
`;
const SubSectionLabel = styled.Text `
  font-size: 14px;
  font-family: ${() => font.pretendardBold};
  color: ${color.gray70};
`;
const SubSectionTitle = styled.Text `
  font-size: 20px;
  line-height: 32px;
  margin-top: 8px;
  font-family: ${() => font.pretendardBold};
  color: ${color.black};
`;
const PhoneRow = styled.View `
  margin-top: 8px;
  flex-direction: row;
  align-items: center;
  gap: 12px;
`;
const PhoneNumberText = styled.Text `
  font-size: 20px;
  line-height: 32px;
  font-family: ${() => font.pretendardBold};
  color: ${color.black};
`;
const SubSectionDescription = styled.Text `
  font-size: 16px;
  margin-top: 4px;
  font-family: ${font.pretendardRegular};
  color: ${color.gray90};
`;
const RecommendedAccessRoute = styled.View `
  width: 100%;
  border-radius: 14px;
  border-color: ${color.brandColor};
  border-width: 1.5px;
  flex-direction: column;
  padding: 16px;
`;
const ExtraInfos = styled.View `
  width: 100%;
  background-color: ${color.gray10};
  padding: 20px;
  border-radius: 20px;
`;
const Footer = styled.View `
  width: 100%;
  background-color: ${color.gray10};
  padding-top: 20px;
  padding-left: 20px;
  padding-right: 20px;
  padding-bottom: 30px;
`;
const FooterText = styled.Text `
  font-size: 10px;
  font-family: ${() => font.pretendardRegular};
  color: ${color.gray80};
`;
const TitleArea = styled.View `
  gap: 12px;
  align-items: flex-start;
  display: flex;
  flex-direction: column;
`;
const RegistrantRow = styled.View `
  flex-direction: row;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
`;
const RegistrantName = styled.Text `
  font-size: 12px;
  line-height: 16px;
  font-family: ${() => font.pretendardMedium};
  color: ${color.brand50};
`;
const RegistrantDate = styled.Text `
  font-size: 12px;
  line-height: 16px;
  font-family: ${() => font.pretendardRegular};
  color: ${color.gray60};
`;
const AddressRow = styled.View `
  flex-direction: row;
  align-items: center;
  gap: 4px;
  flex-shrink: 1;
`;
const DirectionsRow = styled.View `
  flex-direction: row;
  align-items: center;
  gap: 4px;
  margin-top: 0px;
`;
const DirectionsText = styled.Text `
  font-family: ${() => font.pretendardMedium};
  font-size: 14px;
  color: ${() => color.gray70};
  text-decoration-line: underline;
`;
const AddressText = styled.Text `
  font-size: 14px;
  font-family: ${() => font.pretendardBold};
  color: ${color.gray70};
  flex-shrink: 1;
`;
const CopyButton = styled(SccTouchableOpacity) `
  gap: 4px;
  flex-direction: row;
  align-items: center;
`;
const CopyText = styled.Text `
  font-size: 14px;
  font-family: ${() => font.pretendardMedium};
  color: ${color.brandColor};
`;
const TextButton = styled(SccTouchableOpacity) `
  flex-grow: 1;
  align-items: center;
  flex-direction: row;
  justify-content: center;
  gap: 4px;
`;
const VerticalDivider = styled.View `
  width: 1px;
  height: 22px;
  background-color: ${color.gray20};
`;
const TextButtonText = styled.Text `
  font-size: 14px;
  font-family: ${() => font.pretendardRegular};
  color: ${color.black};
`;
const TextButtonContainer = styled.View `
  align-items: center;
  flex-direction: row;
  gap: 8px;
`;
export default ToiletDetailScreen;
